import CRMOrderDetails from '../components/CRMOrderDetails';
import StaffOrderItemForm from '../components/StaffOrderItemForm';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Package, LogOut, Clock, CheckCircle, ChevronDown, ChevronUp, Edit2, Save, X, Image, Upload, FolderOpen, Paintbrush, RefreshCw, FileText, CreditCard, ArrowRight, Settings, Star, MessageSquare } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import OrderItemDetails from '../components/OrderItemDetails';
import OrderPaymentSummary from '../components/OrderPaymentSummary';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_production: 'bg-purple-100 text-purple-800',
  ready_for_pickup: 'bg-green-100 text-green-800',
  completed: 'bg-emerald-100 text-emerald-800',
  shipped: 'bg-teal-100 text-teal-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

const PortalDashboard = () => {
  const { customer, token, logout, updateProfile, changePassword } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [orderError, setOrderError] = useState('');
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [tab, setTab] = useState('orders'); // orders | profile | password
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Testimonial state
  const [testimonialForm, setTestimonialForm] = useState({
    author_name: '',
    content: '',
    rating: 0,
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingTestimonial, setSubmittingTestimonial] = useState(false);
  const [myTestimonials, setMyTestimonials] = useState([]);
  const [testimonialMsg, setTestimonialMsg] = useState('');
  const [testimonialErr, setTestimonialErr] = useState('');

  useEffect(() => {
    if (!customer) { navigate('/portal/login'); return; }
    if (customer.must_change_password) { navigate('/portal/set-password'); return; }
    setProfileForm({ first_name: customer.first_name, last_name: customer.last_name, phone: customer.phone || '' });
    setTestimonialForm(p => ({ ...p, author_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() }));
    fetchOrders();
    fetchQuotes();
    fetchMyTestimonials();
  }, [customer]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/portal/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(res.data);
      setOrderError('');
    } catch { setOrderError('We couldn’t refresh your orders. Please try again.'); } finally { setLoadingOrders(false); }
  };

  useEffect(() => {
    if (!token) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') fetchOrders(); };
    const timer = setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [token]);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/quotes/customer/my-quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(res.data);
    } catch {} finally { setLoadingQuotes(false); }
  };

  const fetchMyTestimonials = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/portal/testimonials/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setMyTestimonials(res.data || []);
    } catch {}
  };

  const handleSubmitTestimonial = async (e) => {
    e.preventDefault();
    setTestimonialErr('');
    setTestimonialMsg('');
    if (!testimonialForm.author_name.trim()) { setTestimonialErr('Please enter your name'); return; }
    if (testimonialForm.content.trim().length < 10) { setTestimonialErr('Please write at least 10 characters'); return; }
    if (testimonialForm.rating < 1) { setTestimonialErr('Please select a star rating'); return; }
    setSubmittingTestimonial(true);
    try {
      const res = await axios.post(`${API_URL}/api/portal/testimonials`, {
        author_name: testimonialForm.author_name.trim(),
        content: testimonialForm.content.trim(),
        rating: testimonialForm.rating,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonialMsg(res.data.message || 'Thanks! Your testimonial has been submitted.');
      setTestimonialForm({
        author_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
        content: '',
        rating: 0,
      });
      fetchMyTestimonials();
    } catch (ex) {
      setTestimonialErr(ex.response?.data?.detail || 'Failed to submit testimonial. Please try again.');
    } finally {
      setSubmittingTestimonial(false);
    }
  };

  const handleProfileSave = async () => {
    setErr(''); setMsg('');
    try {
      await updateProfile(profileForm);
      setEditing(false);
      setMsg('Profile updated');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to update'); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setErr(''); setMsg('');
    if (pwForm.newPw !== pwForm.confirm) { setErr('Passwords do not match'); return; }
    if (pwForm.newPw.length < 8) { setErr('Password must be at least 8 characters'); return; }
    try {
      await changePassword(pwForm.current, pwForm.newPw);
      setMsg('Password changed successfully');
      setPwForm({ current: '', newPw: '', confirm: '' });
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to change password'); }
  };

  const activeOrders = orders.filter(o => !['delivered', 'completed', 'cancelled'].includes(o.status));
  const pastOrders = orders.filter(o => ['delivered', 'completed', 'cancelled'].includes(o.status));

  if (!customer) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="portal-dashboard">
      {/* Welcome Banner */}
      <div className="bg-[#1B3A5C] py-5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Welcome, {customer.first_name}</h1>
            <p className="text-white/50 text-xs">{customer.email}</p>
          </div>
          <button onClick={() => { logout(); navigate('/portal/login'); }}
            className="flex items-center gap-2 px-3 py-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-xs"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-white rounded-xl p-1 shadow-sm mb-8 max-w-2xl mx-auto" data-testid="portal-tabs">
          {[
            { key: 'orders', label: 'My Orders', icon: Package },
            { key: 'quotes', label: 'My Quotes', icon: FileText },
            { key: 'media', label: 'Media Library', icon: Image },
            { key: 'testimonial', label: 'Write a Testimonial', icon: Star },
            { key: 'profile', label: 'Profile', icon: User },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setErr(''); setMsg(''); }}
              className={`flex-1 min-w-[100px] min-h-[44px] flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === t.key ? 'bg-[#1B3A5C] text-white shadow' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {msg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 text-sm">{msg}</div>}
        {err && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">{err}</div>}

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div>
            {orderError && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{orderError}<button onClick={fetchOrders} className="ml-3 min-h-[44px] underline font-semibold">Retry</button></div>}
                {loadingOrders ? (
              <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />)}</div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No orders yet</p>
                <Link to="/back-to-school" className="text-[#4A90A4] hover:underline text-sm">Browse products</Link>
              </div>
            ) : (
              <>
                {activeOrders.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-[#1B3A5C] mb-4 flex items-center gap-2"><Clock className="w-5 h-5" /> Active Orders</h2>
                    <div className="space-y-3">{activeOrders.map(o => <OrderCard key={o.id} order={o} expanded={expandedOrder === o.id} onToggle={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)} />)}</div>
                  </div>
                )}
                {pastOrders.length > 0 && (
                  <div>
                    <h2 className="text-lg font-bold text-gray-500 mb-4 flex items-center gap-2"><CheckCircle className="w-5 h-5" /> Past Orders</h2>
                    <div className="space-y-3">{pastOrders.map(o => <OrderCard key={o.id} order={o} expanded={expandedOrder === o.id} onToggle={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)} />)}</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {tab === 'quotes' && (
          <div data-testid="quotes-tab">
            {loadingQuotes ? (
              <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />)}</div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No quotes yet</p>
                <Link to="/quote/builder" className="text-[#4A90A4] hover:underline text-sm">Get a Quote</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {quotes.map(q => {
                  const isPending = q.status === 'pending';
                  const isPaid = q.status === 'paid';
                  const isConverted = q.status === 'converted';
                  const isExpired = q.is_expired || q.expiry_state === 'expired';
                  const expiringSoon = q.expiry_state === 'expiring_soon';
                  const days = q.days_until_expiry;
                  return (
                    <div key={q.id} className={`bg-white rounded-xl p-5 shadow-sm border ${isExpired ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`} data-testid={`quote-card-${q.id}`}>
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-[#1B3A5C] text-sm">{q.quote_number}</span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            isPaid ? 'bg-green-100 text-green-700' : isPending ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isConverted ? 'Converted to order' : isPaid ? 'Paid' : isPending ? 'Open' : q.status}
                          </span>
                          {q.rush_option && q.rush_option !== 'standard' && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">Rush</span>
                          )}
                          {isPending && isExpired && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700" data-testid={`quote-expired-${q.id}`}>
                              Expired
                            </span>
                          )}
                          {isPending && expiringSoon && days !== null && days >= 0 && (
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${days <= 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`quote-expiry-badge-${q.id}`}>
                              {days === 0 ? 'Expires today' : `Expires in ${days} day${days !== 1 ? 's' : ''}`}
                            </span>
                          )}
                          {q.paid_in_store && (
                            <span className="px-2 py-0.5 bg-[#1B3A5C]/10 text-[#1B3A5C] text-[10px] font-bold uppercase rounded-full">
                              Paid in-store
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <div className="flex items-end justify-between flex-wrap gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span>Total: <strong className="text-[#1B3A5C]">${q.total?.toFixed(2)}</strong></span>
                            <span>Deposit: <strong className="text-[#F5A623]">${q.deposit_amount?.toFixed(2)}</strong></span>
                          </div>
                          {isExpired && (
                            <p className="text-xs text-red-600">
                              Contact us at <a href="mailto:info@shopkyis.com" className="underline">info@shopkyis.com</a> for a new quote or extension.
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {isConverted && (
                            <button onClick={() => setTab('orders')}
                              className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-[#1B3A5C] font-semibold text-xs rounded-full">
                              View order &amp; balance
                            </button>
                          )}
                          {isPending && !isExpired && (
                            <Link
                              to={`/quote/builder?edit=${q.id}`}
                              className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 font-semibold text-xs rounded-full hover:border-[#4A90A4] hover:text-[#4A90A4] transition"
                              data-testid={`edit-quote-${q.id}`}
                            >
                              <Edit2 className="w-3.5 h-3.5" /> Edit
                            </Link>
                          )}
                          {isPending && !isExpired && (
                            <button
                              onClick={async () => {
                                try {
                                  const res = await axios.post(`${API_URL}/api/quotes/${q.id}/checkout`,
                                    { origin_url: window.location.origin },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  window.location.href = res.data.url;
                                } catch { setErr('Failed to start checkout'); }
                              }}
                              className="inline-flex items-center gap-1.5 px-5 py-2 bg-[#F5A623] text-[#0A1C29] font-bold text-xs rounded-full hover:bg-[#D88F1B] transition shadow-sm"
                              data-testid={`pay-quote-${q.id}`}
                            >
                              <CreditCard className="w-3.5 h-3.5" /> Check Out — Pay Deposit <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                          {isPaid && (
                            <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
                              <CheckCircle className="w-3.5 h-3.5" /> Deposit Paid
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Media Library Tab */}
        {tab === 'media' && (
          <div className="bg-white rounded-2xl p-8 shadow-sm" data-testid="media-library-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#1B3A5C] flex items-center gap-2">
                <FolderOpen className="w-5 h-5" /> Media Library
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">Artwork attached to your orders. Open an order to upload more files.</p>
            {orders.flatMap(order => (order.uploads || []).map(file => ({ ...file, order_number: order.order_number }))).length === 0 ? <p className="py-8 text-gray-600">No artwork uploaded yet.</p> :
              <ul className="divide-y divide-gray-100">{orders.flatMap(order => (order.uploads || []).map(file => (
                <li key={`${order.id}-${file.id}`} className="py-3 flex flex-wrap gap-2 justify-between text-sm">
                  <a className="text-[#1B3A5C] underline break-all" href={file.file_url?.startsWith('/') ? `${API_URL}${file.file_url}` : file.file_url} target="_blank" rel="noreferrer">{file.file_name}</a>
                  <span className="text-gray-500">Order {order.order_number}</span>
                </li>
              )))}</ul>}

          </div>
        )}

        {/* Testimonial Tab */}
        {tab === 'testimonial' && (
          <div className="max-w-2xl mx-auto" data-testid="testimonial-tab">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="flex items-start gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#F5A623]/15 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-[#F5A623]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1B3A5C]">Share your experience</h2>
                  <p className="text-gray-500 text-sm mt-0.5">
                    Tell us how we did — your review helps other customers and will appear on our site after a quick review.
                  </p>
                </div>
              </div>

              {testimonialMsg && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-5 text-sm" data-testid="testimonial-success">
                  {testimonialMsg}
                </div>
              )}
              {testimonialErr && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 text-sm" data-testid="testimonial-error">
                  {testimonialErr}
                </div>
              )}

              <form onSubmit={handleSubmitTestimonial} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Name</label>
                  <input
                    type="text" required
                    value={testimonialForm.author_name}
                    onChange={e => setTestimonialForm(p => ({ ...p, author_name: e.target.value }))}
                    placeholder="How would you like to be credited?"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900"
                    data-testid="testimonial-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
                  <div className="flex items-center gap-1" data-testid="testimonial-stars" role="radiogroup" aria-label="Rating">
                    {[1, 2, 3, 4, 5].map(star => {
                      const active = (hoverRating || testimonialForm.rating) >= star;
                      return (
                        <button
                          key={star}
                          type="button"
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setTestimonialForm(p => ({ ...p, rating: star }))}
                          className="p-1 -m-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90A4] rounded"
                          aria-label={`${star} star${star !== 1 ? 's' : ''}`}
                          aria-checked={testimonialForm.rating === star}
                          role="radio"
                          data-testid={`testimonial-star-${star}`}
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${active ? 'text-[#F5A623] fill-[#F5A623]' : 'text-gray-300'}`}
                          />
                        </button>
                      );
                    })}
                    <span className="ml-3 text-sm text-gray-500" data-testid="testimonial-rating-text">
                      {testimonialForm.rating > 0 ? `${testimonialForm.rating} / 5` : 'Tap a star'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Review</label>
                  <textarea
                    required rows={5}
                    value={testimonialForm.content}
                    onChange={e => setTestimonialForm(p => ({ ...p, content: e.target.value }))}
                    placeholder="What did you order? How was the quality and service?"
                    maxLength={1000}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A90A4] focus:border-transparent outline-none text-gray-900 resize-y"
                    data-testid="testimonial-content"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{testimonialForm.content.length}/1000</p>
                </div>

                <button
                  type="submit"
                  disabled={submittingTestimonial}
                  className="w-full sm:w-auto px-8 py-3.5 bg-[#F5A623] text-white font-bold rounded-xl hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="testimonial-submit"
                >
                  {submittingTestimonial ? 'Submitting...' : <><Star className="w-4 h-4 fill-current" /> Submit Testimonial</>}
                </button>
              </form>
            </div>

            {/* My past submissions */}
            {myTestimonials.length > 0 && (
              <div className="mt-8" data-testid="my-testimonials-list">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Your Submissions</h3>
                <div className="space-y-3">
                  {myTestimonials.map(t => (
                    <div key={t.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-testid={`my-testimonial-${t.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-4 h-4 ${s <= (t.rating || 0) ? 'text-[#F5A623] fill-[#F5A623]' : 'text-gray-200'}`} />
                          ))}
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {t.is_active ? 'Published' : 'Pending review'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{t.content}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {tab === 'profile' && (
          <div className="bg-white rounded-2xl p-8 shadow-sm max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#1B3A5C]">Contact Information</h2>
              {!editing && (
                <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-[#4A90A4] hover:underline" data-testid="edit-profile-btn">
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
              )}
            </div>
            {editing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">First Name</label>
                    <input value={profileForm.first_name} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4A90A4] outline-none text-gray-900" data-testid="profile-first-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Last Name</label>
                    <input value={profileForm.last_name} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4A90A4] outline-none text-gray-900" data-testid="profile-last-name" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                  <input value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4A90A4] outline-none text-gray-900" data-testid="profile-phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                  <input value={customer.email} disabled className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={handleProfileSave} className="flex items-center gap-1 px-5 py-2 bg-[#F5A623] text-white font-bold rounded-lg hover:brightness-110" data-testid="save-profile-btn">
                    <Save className="w-4 h-4" /> Save
                  </button>
                  <button onClick={() => setEditing(false)} className="flex items-center gap-1 px-5 py-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">
                    <X className="w-4 h-4" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-gray-400 mb-0.5">First Name</p><p className="font-medium text-gray-900" data-testid="display-first-name">{customer.first_name}</p></div>
                  <div><p className="text-xs text-gray-400 mb-0.5">Last Name</p><p className="font-medium text-gray-900" data-testid="display-last-name">{customer.last_name}</p></div>
                </div>
                <div><p className="text-xs text-gray-400 mb-0.5">Email</p><p className="font-medium text-gray-900">{customer.email}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Phone</p><p className="font-medium text-gray-900">{customer.phone || '—'}</p></div>
              </div>
            )}

            {/* Change Password section */}
            <div className="mt-10 pt-8 border-t border-gray-100">
              <h3 className="text-md font-bold text-[#1B3A5C] mb-4">Change Password</h3>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <input type="password" placeholder="Current password" value={pwForm.current}
                  onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4A90A4] outline-none text-gray-900" data-testid="pw-current" />
                <input type="password" placeholder="New password (min 8 chars)" value={pwForm.newPw}
                  onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4A90A4] outline-none text-gray-900" data-testid="pw-new" />
                <input type="password" placeholder="Confirm new password" value={pwForm.confirm}
                  onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4A90A4] outline-none text-gray-900" data-testid="pw-confirm" />
                <button type="submit" className="px-5 py-2 bg-[#1B3A5C] text-white font-bold rounded-lg hover:bg-[#152e4a] text-sm" data-testid="change-pw-btn">
                  Update Password
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Order Card ─── */
const DESIGN_ELIGIBLE_STATUSES = ['pending', 'confirmed', 'in_production'];

export const OrderCard = ({ order, expanded, onToggle }) => {
  const { token, customer } = useCustomerAuth();
  const statusColor = STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800';
  const designEligible = order.items?.length > 0 && DESIGN_ELIGIBLE_STATUSES.includes(order.status);
  // Preserve the prior behavior while an older API response is in flight;
  // only an explicit false marks a school order as artwork-exempt.
  const artworkRequired = order.artwork_required !== false;
  const needsConfig = order.config_status && !['complete', 'not_applicable'].includes(order.config_status) && designEligible;

  // Artwork/logo uploads attached to this order (visible to the KYIS team)
  const [uploadingOrderId, setUploadingOrderId] = useState(null);
  const [extraUploads, setExtraUploads] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const allUploads = [...(order.uploads || []), ...extraUploads];

  const handleArtworkUpload = async (orderId, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingOrderId(orderId);
    setUploadError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/api/orders/${orderId}/upload`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExtraUploads((prev) => [...prev, res.data]);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Upload failed — please try again.');
    } finally {
      setUploadingOrderId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`order-${order.order_number}`}>
      <button onClick={onToggle} aria-expanded={expanded} className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-4 min-w-0 flex-wrap">
          <div className="min-w-0 break-words">
            <p className="font-bold text-[#1B3A5C] text-sm">#{order.in_store_ticket || order.order_number}</p>
            <p className="text-xs text-gray-500">{order.order_source === 'in_store' ? 'In-store order' : 'Online order'}</p>
            <p className="text-xs text-gray-400">{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
            {order.pipeline_stage || (order.status || 'pending').replaceAll('_', ' ')}
          </span>
          {needsConfig && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#F5A623]/15 text-[#B87510] uppercase tracking-wider"
              data-testid={`needs-config-${order.order_number}`}>
              Action needed
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <span className="font-bold text-gray-900">${order.total?.toFixed(2)}</span>
            <p className="text-xs text-gray-600">{order.payment_review_required ? 'Balance under review' : order.payment_status === 'Paid in Full' ? 'Paid in full' : `$${Number(order.balance_due || 0).toFixed(2)} due`}</p>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-50">
          {/* Configure Items CTA — shown first when items need configuration */}
          {designEligible && (
            <a href={`/portal/orders/${order.id}/configure`}
              className={`mt-4 flex items-center justify-between w-full p-3 rounded-xl hover:shadow-md transition group
                ${needsConfig
                  ? 'bg-gradient-to-r from-[#F5A623] to-[#E8961B] text-white'
                  : 'bg-gradient-to-r from-[#4A90A4] to-[#3a7183] text-white'}`}
              data-testid={`configure-btn-${order.order_number}`}>
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <div>
                  <p className="font-semibold text-sm">{needsConfig ? 'Configure Your Items' : 'Review / Update Configuration'}</p>
                  <p className="text-[10px] text-white/80">
                    {needsConfig
                      ? (artworkRequired ? 'Choose sizes, colors & logos for this order' : 'Choose sizes and colors for this order')
                      : (artworkRequired ? 'Update sizes, colors or logos for this order' : 'Update sizes or colors for this order')}
                  </p>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 -rotate-90 group-hover:translate-x-1 transition-transform" />
            </a>
          )}
          {/* Design Builder CTA */}
          {designEligible && artworkRequired && (
            <a href={`/portal/orders/${order.id}/design`}
              className="mt-3 flex items-center justify-between w-full p-3 bg-gradient-to-r from-[#1B3A5C] to-[#4A90A4] text-white rounded-xl hover:shadow-md transition group"
              data-testid={`design-btn-${order.order_number}`}>
              <div className="flex items-center gap-3">
                <Paintbrush className="w-5 h-5" />
                <div>
                  <p className="font-semibold text-sm">Create Your Design</p>
                  <p className="text-[10px] text-white/70">Build your embroidery artwork for this order</p>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 -rotate-90 group-hover:translate-x-1 transition-transform" />
            </a>
          )}
          <div className="mt-4 space-y-2">
            {order.items?.map((item, i) => (
              <div key={item.id || i} className="text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{item.product_name}{item.variant_name ? ` — ${item.variant_name}` : ''} x{item.quantity}</span>
                  <span className="font-medium text-gray-900">${item.total_price?.toFixed(2)}</span>
                </div>
                <OrderItemDetails item={item} />
              </div>
            ))}
          </div>

          <CRMOrderDetails details={order.crm_details} />
          <StaffOrderItemForm order={order} />
          {/* Artwork & logo files for this order */}
          {(artworkRequired || customer?.staff_access || allUploads.length > 0) && <div className="mt-4 pt-3 border-t border-gray-50" data-testid={`order-artwork-${order.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[#4A90A4]">Artwork &amp; Logos</span>
              <label className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-colors ${
                uploadingOrderId === order.id ? 'bg-gray-100 text-gray-400' : 'bg-[#F5A623]/10 text-[#B47800] hover:bg-[#F5A623]/20'}`}>
                <Upload className="w-3.5 h-3.5" />
                {uploadingOrderId === order.id ? 'Uploading…' : 'Upload file'}
                <input type="file" className="hidden" accept="image/*,.pdf,.ai,.eps,.svg"
                  disabled={uploadingOrderId === order.id}
                  onChange={(e) => handleArtworkUpload(order.id, e)}
                  data-testid={`artwork-input-${order.id}`} />
              </label>
            </div>
            {uploadError && <p className="text-xs text-red-500 mb-2">{uploadError}</p>}
            {allUploads.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {allUploads.map((u) => (
                  <a key={u.id} href={u.file_url?.startsWith('/') ? `${API_URL}${u.file_url}` : u.file_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-gray-100 bg-gray-50 hover:border-[#4A90A4]/40 transition-colors">
                    {(u.file_type || '').startsWith('image/') ? (
                      <img src={u.file_url?.startsWith('/') ? `${API_URL}${u.file_url}` : u.file_url} alt="" className="w-8 h-8 rounded object-contain bg-white border border-gray-100" />
                    ) : (
                      <FileText className="w-6 h-6 text-[#4A90A4]" />
                    )}
                    <span className="text-xs text-gray-600 max-w-[140px] truncate">{u.file_name}</span>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No files yet — upload your logo or artwork so our team can get started.</p>
            )}
          </div>}
          <div className="mt-4 pt-3 border-t border-gray-50 text-sm space-y-1">
            {order.fulfillment_method && (
              <div className="flex justify-between text-gray-600 pb-1">
                <span className="font-medium">Fulfillment</span>
                <span className="font-semibold text-[#1B3A5C]">{{
                  in_store_pickup: 'In-Store Pickup',
                  shipping: 'Shipping',
                  delivery: 'Local Delivery',
                  pop_up_pickup: 'Pop-Up Pickup',
                }[order.fulfillment_method] || order.fulfillment_method}</span>
              </div>
            )}
            {order.order_source !== 'in_store' && <><div className="flex justify-between text-gray-500"><span>Subtotal</span><span>${order.subtotal?.toFixed(2)}</span></div>
            <div className="flex justify-between text-gray-500"><span>Tax</span><span>${order.tax?.toFixed(2)}</span></div>
            <div className="flex justify-between text-gray-500"><span>Shipping</span><span>${order.shipping_cost?.toFixed(2)}</span></div>
            {order.is_rush && <div className="flex justify-between text-[#F5A623]"><span>Rush Fee</span><span>$25.00</span></div>}
            </>}
            <div className="flex justify-between font-bold text-[#1B3A5C] pt-1"><span>Total</span><span>${order.total?.toFixed(2)}</span></div>
          </div>
          <OrderPaymentSummary order={order} token={token} />
          {order.notes && <p className="mt-3 text-xs text-gray-400 italic">Notes: {order.notes}</p>}
          {/* Reorder Button */}
          {order.items?.length > 0 && <a href={`/portal/orders/${order.id}/reorder`}
            className="mt-4 flex items-center justify-between w-full p-3 bg-gradient-to-r from-[#F5A623] to-[#E8961B] text-white rounded-xl hover:shadow-md transition group"
            data-testid={`reorder-btn-${order.order_number}`}>
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5" />
              <div>
                <p className="font-semibold text-sm">Reorder</p>
                <p className="text-[10px] text-white/70">Same items & pricing — adjust quantities</p>
              </div>
            </div>
            <ChevronDown className="w-4 h-4 -rotate-90 group-hover:translate-x-1 transition-transform" />
          </a>}
        </div>
      )}
    </div>
  );
};

export default PortalDashboard;
